import { useGetAccountByIdQuery } from "@/modules/account/services";
import { AppPaths, CloudflareAsset, SideSheetPosition } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, DetailActions, EmptyOrErrorContent } from "@/shared/components";
import { buildRoute, getErrorMessage } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AccountDetailHistory } from "./AccountDetailHistory";
import { AccountDetailRows } from "./AccountDetailRows";
import { AccountRenameDialog } from "./AccountRenameDialog";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
}

export const AccountDetails = ({ isOpen, onClose, accountId }: Props) => {
  const { t } = useTranslation();
  const featureToggle = useAppSelector((state) => state.featureToggle.toggles);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery();

  const [isOpenRenameDialog, setIsOpenRenameDialog] = useState<boolean>(false);

  const {
    data: account,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetAccountByIdQuery({ accountId });

  const onCloseRenameDialog = () => setIsOpenRenameDialog(false);

  const onOpenRenameDialog = () => setIsOpenRenameDialog(true);

  const navigateToStatementPage = () => {
    if (!account?.name) return;
    const url = buildRoute(
      AppPaths.AccountStatement,
      { accountId }
    );
    navigate(url);
  };

  const navigateToRequisitesPage = () => {
    if (!account?.name) return;
    const url = buildRoute(
      AppPaths.AccountRequisites,
      { accountId }
    );
    navigate(url);
  };

  const actions = [
    {
      id: 0,
      title: t("general.statement.title"),
      icon: CloudflareAsset.DocumentDeclaration,
      isAvailable: featureToggle.isAccountStatementAvailable,
      onClick: () => navigateToStatementPage(),
    },
    {
      id: 1,
      title: t("general.requisite.title"),
      icon: CloudflareAsset.DocumentList,
      isAvailable: featureToggle.isAccountRequisiteAvailable,
      onClick: () => navigateToRequisitesPage(),
    }
  ].filter((action) => action.isAvailable);

  return (
    <SideSheet
      position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
      isPositioned={!isDesktop}
      withOverlay={!isDesktop}
      isOpen={isOpen}
      isBodyHidden={false}
      onClose={onClose}
      isWithDivider
      isLoading={isFetching}
      className="detail-side-sheet"
      size="small"
      topLeftActions={
        !isFetching && !isError ? (
          <Button
            type="tertiary"
            size="small"
            className="ml-8"
            data-testid="rename-account"
            data-ga-id="rename-account"
            data-ga-action="Open rename account dialog"
            onClick={onOpenRenameDialog}
            iconProps={{
              Component: (props: ISVGIconProps) => (
                <CloudFlareImage {...props} path={CloudflareAsset.Edit}/>
              ),
            }}/>
        ) : null
      }
      title={account?.name}
      shouldRemoveCallback={false}
      closeOnOutsideClick={false}
    >
      {({ scrollbarContainerRef }) => (
        <>
          {isFetching ? (
            <Spinner isFullScreen={false} size="small"/>
          ) : isError && !isFetching ? (
            <div className="full-width full-height flexbox align-items--center">
              <EmptyOrErrorContent
                isError={true}
                title={t(getErrorMessage(error as IError))}
                action={refetch}
              />
            </div>
          ) : (
            <>
              <AccountDetailRows
                account={account}
                scrollbarContainerRef={scrollbarContainerRef}
              />
              <DetailActions actions={actions} page="Account detail"/>
              {accountId && (
                <AccountDetailHistory accountId={accountId}/>
              )}
              <AccountRenameDialog
                refetch={refetch}
                accountNumber={account?.number}
                name={account?.name}
                isOpen={isOpenRenameDialog}
                onClose={onCloseRenameDialog}
              />
            </>
          )}
        </>
      )}
    </SideSheet>
  );
};
